import React, { useState, useEffect, useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { RiCloseLine } from "react-icons/ri";

import Button from "../../../../../../../components/Form/Button";
import { Despatch } from "../../../../../../../models/despatch";
import { DespatchViewButtonItem, DespatchViewTab, DespatchViewNavigation } from "../../types";
import EmptySection from "../EmptySection";
import {
  Sidebar,
  SidebarList,
  SidebarListItem,
  SidebarTitle,
  Wrapper,
  Main,
  MainHeader,
  MainHeaderTitle,
  MainHeaderToggle,
  MainScreen,
  MainTitle,
  MainComponent,
  SidebarButtonsWrapper,
  SidebarButtonText,
} from "./styles";

interface Props {
  title: string;
  isLoading: boolean;
  tabs: DespatchViewTab[];
  buttonsComponent?: DespatchViewButtonItem[];
  despatch: Despatch | null;
  isOpened: boolean;
  onClose: () => void;
}

const DespatchSideViewComponent: React.FC<Props> = (props) => {
  const { tabs, isLoading, title, buttonsComponent, isOpened, onClose } = props;
  const { t: translate } = useTranslation();
  // const classes = useStyles();

  const [extraComponent, setExtraComponent] = useState<DespatchViewButtonItem>();
  const [currentLink, setCurrentLink] = useState<string>(DespatchViewNavigation.DETAILS);

  const navigation = useMemo(() => {
    return tabs.map((tab) => ({
      key: tab.key,
    }));
  }, [tabs]);

  const handleScrollIntoView = (id: string) => {
    const element = document.getElementById(id);

    setCurrentLink(id);

    element?.scrollIntoView({ behavior: "smooth" });
  };

  const handleWheel = useCallback(
    (event) => {
      let nextIndex;

      if (event.wheelDelta <= 0) {
        nextIndex = navigation.findIndex((nav) => nav.key === currentLink) + 1;
      } else {
        nextIndex = navigation.findIndex((nav) => nav.key === currentLink) - 1;
      }

      if (nextIndex !== undefined && nextIndex <= navigation.length - 1 && nextIndex >= 0) {
        const nextPage = navigation[nextIndex]?.key;
        const nextPageTop = document?.getElementById(nextPage)?.getBoundingClientRect()?.top;
        const isToShow =
          nextPageTop && event.wheelDelta <= 0
            ? nextPageTop <= 200
            : nextPageTop && nextPageTop >= 95;

        if (nextPageTop && isToShow) setCurrentLink(nextPage);
      }
    },
    [currentLink]
  );

  const renderSection = (tab: DespatchViewTab) => {
    if (tab.isEmpty) return <EmptySection />;

    return tab.render();
  };

  useEffect(() => {
    document?.getElementById("componentSection")?.addEventListener("wheel", handleWheel);

    return () => {
      document.getElementById("componentSection")?.removeEventListener("wheel", handleWheel);
    };
  });

  return (
    <Wrapper
      anchor="right"
      open={isOpened}
      onClose={() => {
        setCurrentLink(DespatchViewNavigation.DETAILS);
        if (onClose) onClose();
      }}
      data-cy="sidebar-container"
    >
      <Sidebar data-cy="sidebar-navlist">
        <SidebarTitle>
          <span data-cy="sidebar-navlist-title">{translate("navigation")}</span>
        </SidebarTitle>
        <SidebarList data-cy="sidebar-navlist-list">
          {navigation.map((menuItem) => (
            <SidebarListItem
              id={`item-${menuItem.key}`}
              data-cy={`sidebar-navlist-item-${menuItem.key}`}
              is_loading={isLoading}
              className={`${menuItem.key === currentLink ? "active" : ""}`}
              key={menuItem.key}
              onClick={() => {
                if (isLoading) return;
                setExtraComponent(undefined);
                setTimeout(() => handleScrollIntoView(menuItem.key), 500);
              }}
            >
              <span>{tabs.find((tab) => tab.key === menuItem.key)?.label}</span>
            </SidebarListItem>
          ))}
        </SidebarList>
        <SidebarButtonsWrapper>
          {buttonsComponent?.map((button, index: number) => (
            <Button
              key={index}
              data-cy={button.buttonLabel}
              className={button.class}
              customColor={button.color}
              disabled={isLoading}
              onClick={() => {
                setExtraComponent(button);
                setCurrentLink("");
              }}
            >
              {button.icon}
              <SidebarButtonText>{button.buttonLabel}</SidebarButtonText>
            </Button>
          ))}
        </SidebarButtonsWrapper>
      </Sidebar>

      <Main data-cy="sidebar-page">
        <MainHeader data-cy="sidebar-page-header">
          <MainHeaderTitle data-cy="sidebarTitle">{title}</MainHeaderTitle>
          <MainHeaderToggle data-cy="iconButtonClose" onClick={() => onClose()}>
            <RiCloseLine color="#308CDB" />
          </MainHeaderToggle>
        </MainHeader>
        <MainScreen id="componentSection" data-cy="sidebar-page-screen">
          {extraComponent && (
            <div data-cy={`component-${extraComponent.titlePage}`}>
              <MainTitle data-cy="componentTitlePage">{extraComponent.titlePage}</MainTitle>
              <MainComponent data-cy="componentPage">
                {isLoading
                  ? extraComponent.renderLoading && extraComponent.renderLoading()
                  : extraComponent.renderComponent()}
              </MainComponent>
            </div>
          )}

          {!extraComponent &&
            tabs?.map((tab) => (
              <div id={tab.key} key={tab.key}>
                <MainTitle data-cy={`sidebar-page-screen-${tab.key}-title`}>{tab.label}</MainTitle>
                <MainComponent data-cy={`sidebar-page-screen-${tab.key}-component`}>
                  {isLoading ? tab.renderLoading && tab.renderLoading() : renderSection(tab)}
                </MainComponent>
              </div>
            ))}
        </MainScreen>
      </Main>
    </Wrapper>
  );
};

export default DespatchSideViewComponent;
